import React, { useState } from "react";
import { FiCopy, FiEdit2, FiTrash2 } from "react-icons/fi";
import { LoadingSpinner } from "components/Loading/LoadingSpinner";
import { useRoles } from "newui/features/RolesPermissions/hooks/useRoles";
import type { RoleResource } from "newui/features/RolesPermissions/types/RolesPermissions.type";
import { CloneRoleModal } from "./CloneRoleModal";
import { RenameRoleModal } from "./RenameRoleModal";
import { DeleteRoleModal } from "./DeleteRoleModal";

export interface RolesListProps {
	selectedRoleId: number | undefined;
	onSelectRole: (roleId: number) => void;
}

type ActiveModal = "clone" | "rename" | "delete" | null;

export const RolesList = ({ selectedRoleId, onSelectRole }: RolesListProps) => {
	const { data: roles, isLoading } = useRoles();
	const [activeModal, setActiveModal] = useState<ActiveModal>(null);
	const [activeRole, setActiveRole] = useState<RoleResource | null>(null);

	const openModal = (modal: ActiveModal, role: RoleResource, event: React.MouseEvent) => {
		event.stopPropagation();
		setActiveRole(role);
		setActiveModal(modal);
	};

	const closeModal = () => {
		setActiveModal(null);
		setActiveRole(null);
	};

	if (isLoading) {
		return <LoadingSpinner label="Loading roles..." verticalPadding={8} />;
	}

	return (
		<div data-testid="roles-list" className="divide-y divide-gray-200 rounded-md border border-gray-200 bg-white">
			{(roles ?? []).map((role) => {
				const isSelected = role.id === selectedRoleId;

				return (
					<div
						key={role.id}
						data-testid={`role-row-${role.id}`}
						role="button"
						tabIndex={0}
						onClick={() => onSelectRole(role.id)}
						onKeyDown={(e) => {
							if (e.key === "Enter" || e.key === " ") {
								onSelectRole(role.id);
							}
						}}
						className={`flex cursor-pointer items-center justify-between px-4 py-3 text-sm hover:bg-gray-50 ${
							isSelected ? "bg-blue-50" : ""
						}`}
					>
						<div className="flex items-center gap-2">
							<span className="font-medium text-gray-900">{role.name}</span>
							{role.is_base && (
								<span
									data-testid={`role-badge-system-${role.id}`}
									className="rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-600"
								>
									System role
								</span>
							)}
						</div>

						<div className="flex items-center gap-3">
							<button
								type="button"
								data-testid={`clone-role-${role.id}`}
								aria-label={`Clone ${role.name}`}
								className="text-gray-500 hover:text-blue-600"
								onClick={(e) => openModal("clone", role, e)}
							>
								<FiCopy size={16} />
							</button>
							<button
								type="button"
								data-testid={`rename-role-${role.id}`}
								aria-label={`Rename ${role.name}`}
								className={`text-gray-500 hover:text-blue-600 ${role.is_base ? "opacity-40" : ""}`}
								onClick={(e) => openModal("rename", role, e)}
							>
								<FiEdit2 size={16} />
							</button>
							<button
								type="button"
								data-testid={`delete-role-${role.id}`}
								aria-label={`Delete ${role.name}`}
								className={`text-gray-500 hover:text-red-600 ${role.is_base ? "opacity-40" : ""}`}
								onClick={(e) => openModal("delete", role, e)}
							>
								<FiTrash2 size={16} />
							</button>
						</div>
					</div>
				);
			})}

			<CloneRoleModal isOpen={activeModal === "clone"} role={activeRole} onClose={closeModal} />
			<RenameRoleModal isOpen={activeModal === "rename"} role={activeRole} onClose={closeModal} />
			<DeleteRoleModal isOpen={activeModal === "delete"} role={activeRole} onClose={closeModal} />
		</div>
	);
};
